// Self-recovery budget for the OBS overlay page.
//
// Inside OBS nobody is there to press Reload, so after a crash or a failed
// chunk load the page's silent boundary reloads itself. This module caps that
// at MAX_RECOVERY_RELOADS per stretch of trouble: a crash that happens on every
// load stops retrying instead of reloading for the rest of the show, and once
// the page has run healthy for HEALTHY_RESET_MS the budget is given back, so a
// source left running for days can still recover from a later, unrelated
// failure. Counted in sessionStorage, which survives a reload of the same
// Browser Source. Import-cheap: LiveOverlayPage (eager in index.tsx) uses it.

/** At most this many self-reloads before the page has been healthy again. */
export const MAX_RECOVERY_RELOADS = 3;
/** How long the page must run without a crash to get its budget back. */
export const HEALTHY_RESET_MS = 5 * 60 * 1000;
const RELOADS_KEY = "live-overlay-recovery-reloads";

/** Whether another self-reload is allowed; counts it when it is. */
export function claimRecoveryReload(): boolean {
  try {
    const raw = window.sessionStorage.getItem(RELOADS_KEY);
    const used = raw === null ? 0 : Number.parseInt(raw, 10);
    // Anything unreadable counts as spent, so a bad value can never loop.
    if (!Number.isFinite(used) || used >= MAX_RECOVERY_RELOADS) return false;
    window.sessionStorage.setItem(RELOADS_KEY, String(used + 1));
    return true;
  } catch {
    // No storage (blocked or unavailable): the count cannot be kept, so no
    // self-reload at all rather than a possible endless loop.
    return false;
  }
}

/** Gives the budget back after the page has run healthy for a while. */
export function scheduleRecoveryReset(): () => void {
  const id = window.setTimeout(() => {
    try {
      window.sessionStorage.removeItem(RELOADS_KEY);
    } catch {
      // Nothing to reset without storage.
    }
  }, HEALTHY_RESET_MS);
  return () => window.clearTimeout(id);
}
